import React, { useState, useRef } from "react";
import { Map } from "immutable";
import { useDrag, useDrop } from "react-dnd";
import cx from "classnames";
import { Box, Button, Icon, Menu, MenuItem, Separator, Text } from "@showitinc/showit-wc";

import { IMAGE_LIST_ITEM } from "../../../../constants/DnDConstants";
import {
  generateDragSpec,
  generateDropSpec,
  getTargetArea,
  getDropIndexOffset,
} from "../../../../utils/DnDUtils";
import AssetUtils from "../../../../utils/AssetUtils";
import LargeImageListItem from "./LargeImageListItem";
import styles from "./SmallImageListItem.module.css";
import { createInitialDetails, updateDetailsState } from "./GalleryItemUtils";

export const imageListPanelDrop =
  ({ dropRef, index }) =>
  (dragItem, monitor) => {
    const { index: dragIndex } = dragItem;
    const area = getTargetArea(monitor, dropRef);
    const itemOffset = getDropIndexOffset({ area, dragIndex, dropIndex: index });
    const targetIndex = index + itemOffset;

    const data = {
      item: dragItem,
      target: { index: targetIndex, dropArea: area },
    };

    return { area, data };
  };

const SmallImageListItem = (props) => {
  const {
    image: asset = Map(),
    disabled,
    editing,
    index,
    onMoveImage,
    onPropUpdate,
    removeImage,
    onStartEditMode,
  } = props;

  const itemKey = asset.get("key");
  const [dropArea, setDropArea] = useState(null);
  const selfRef = useRef();

  // Update details when asset changes
  const [details, setDetails] = useState(createInitialDetails(asset));

  // Update details when asset changes (e.g. after reordering)
  React.useEffect(() => {
    setDetails(createInitialDetails(asset));
  }, [asset]);

  const drag = generateDragSpec({
    type: IMAGE_LIST_ITEM,
    item: {
      index,
      key: itemKey,
    },
    canDrag: () => !editing && !disabled,
    isDragging: (monitor) => {
      const draggedItem = monitor.getItem();
      return draggedItem.key === itemKey;
    },
    onDrop: ({ data }) => {
      onMoveImage(data);
    },
  });

  const drop = generateDropSpec({
    type: IMAGE_LIST_ITEM,
    canDrop: (item) => !editing && !disabled && item.key !== itemKey,
    onHover: (monitor) => _setDropArea(getTargetArea(monitor, selfRef.current)),
    onDrop: imageListPanelDrop({ index, dropRef: selfRef.current }),
  });

  const [{ isDragging }, dragRef] = useDrag(drag);
  const [{ isOver }, dropRef] = useDrop(drop);
  const previewURL = AssetUtils.getV2PreviewURL(asset.get("key"), {
    provider: asset.get("provider"),
  });

  const _onPropUpdate = (propInfo) => {
    onPropUpdate && onPropUpdate(propInfo);
  };

  const _onImageMove = (data) => {
    onMoveImage && !disabled && onMoveImage(data);
  };

  const _removeImage = (idx) => {
    removeImage && removeImage(idx);
  };

  const _setDropArea = (area) => {
    dropArea !== area && setDropArea(area);
  };

  const _removeThumb = () => {
    removeImage && !disabled && removeImage(index);
  };

  const _onEdit = (key, updated) => {
    setDetails((prevDetails) =>
      updateDetailsState(prevDetails, createInitialDetails(asset), key, updated[key])
    );
  };

  const setRefs = (element) => {
    selfRef.current = element;
    if (!editing) {
      dragRef(element);
      dropRef(element);
    } else {
      dragRef(null);
      dropRef(null);
    }
  };

  return (
    <div
      className={cx([
        styles["image-layer"],
        { [styles[`drag-${dropArea}`]]: isOver && !isDragging && dropArea },
        { [styles.editing]: editing },
        { [styles.editMode]: editing },
      ])}
      ref={setRefs}
    >
      {editing ? (
        <Box className={styles.editorMode}>
          <LargeImageListItem
            disabled={disabled}
            showButtons
            onEdit={_onEdit}
            removeImage={_removeImage}
            image={asset}
            index={index}
            onMoveImage={_onImageMove}
            onPropUpdate={_onPropUpdate}
          />
        </Box>
      ) : (
        <>
          <Icon
            data-testid={`small-image-list-item-drag-icon-${index}`}
            className={styles["drag-icon"]}
            type="dragHandle"
            size="12"
          />
          <div
            data-testid={`small-image-list-item-${index}`}
            className={styles.thumbnail}
            style={{ backgroundImage: `url(${previewURL})` }}
            onClick={onStartEditMode}
          />
          <div className={styles.content} onClick={onStartEditMode}>
            <Text className={styles["details-title"]}>{details.title}</Text>
          </div>
          <Menu shift="left" className={styles.menu}>
            <Button
              variant="icon"
              icon-prefix="menuMoreVert"
              slot="trigger-element"
              iconSize="20"
              size="sm"
            />
            <MenuItem onClick={onStartEditMode}>Edit</MenuItem>
            <Separator variant="context-menu" />
            <MenuItem onClick={_removeThumb}>
              <Box className={styles.delete}>Remove</Box>
            </MenuItem>
          </Menu>
        </>
      )}
    </div>
  );
};

export default SmallImageListItem;
